/**
 * Fixed-capacity event buffer filled by one call to step(). The consumer reads
 * it before the next call. Nothing is allocated when pushing.
 */

import { EV_NAMES, type EvId, IMPULSE_EVENTS } from "./types";

export const EVENT_CAPACITY = 64;

export class EventBuffer {
  count = 0;
  /** Set when an event was dropped because the buffer was full. */
  overflow = 0;
  readonly type = new Int32Array(EVENT_CAPACITY);
  readonly tick = new Int32Array(EVENT_CAPACITY);
  readonly a = new Float64Array(EVENT_CAPACITY);
  readonly b = new Float64Array(EVENT_CAPACITY);
  readonly c = new Float64Array(EVENT_CAPACITY);

  clear(): void {
    this.count = 0;
  }

  push(type: EvId, tick: number, a = 0, b = 0, c = 0): void {
    const i = this.count;
    if (i >= EVENT_CAPACITY) {
      this.overflow++;
      return;
    }
    this.type[i] = type;
    this.tick[i] = tick;
    this.a[i] = a;
    this.b[i] = b;
    this.c[i] = c;
    this.count = i + 1;
  }

  /** True if any event in the buffer marks a deliberate velocity discontinuity. */
  hasImpulse(): boolean {
    for (let i = 0; i < this.count; i++) {
      if (IMPULSE_EVENTS.has(this.type[i]!)) return true;
    }
    return false;
  }

  has(type: EvId): boolean {
    for (let i = 0; i < this.count; i++) if (this.type[i] === type) return true;
    return false;
  }
}

/** A materialized event, for logs and tests (allocates; never used inside step). */
export interface SimEvent {
  type: EvId;
  name: string;
  tick: number;
  a: number;
  b: number;
  c: number;
}

export function drainEvents(buf: EventBuffer, out: SimEvent[]): void {
  for (let i = 0; i < buf.count; i++) {
    const type = buf.type[i] as EvId;
    out.push({
      type,
      name: EV_NAMES[type] ?? String(type),
      tick: buf.tick[i]!,
      a: buf.a[i]!,
      b: buf.b[i]!,
      c: buf.c[i]!,
    });
  }
}
